import 'vue3-perfect-scrollbar/dist/vue3-perfect-scrollbar.css';
import { createHead } from '@vueuse/head';
import { createPinia } from 'pinia';
import piniaPluginPersistedState from 'pinia-plugin-persistedstate';
import vClickOutside from 'v-click-outside';
import { createApp as createVueApp, createSSRApp } from 'vue';
import Vue2TouchEvents from 'vue2-touch-events';
import PerfectScrollbar from 'vue3-perfect-scrollbar';

import Adaptive from '@/utils/system/adaptive';

import { apiManager } from './api/api-manager/api-manager';
import { createFetcher } from './api/fetcher';
import createApi from './api/methods.api';
import { createApiBFF } from './api/methods.apiBFF';
import kit from './components/kit/kit';
import auth from './plugins/auth';
import cities from './plugins/cities';
import featureTogglePlugin from './plugins/feature-toggle-plugin';
import { logger } from './plugins/logger';
import navigation from './plugins/navigation';
import order from './plugins/order';
import persEvents from './plugins/pers-events';
import ScrollIntoView from './plugins/scroll-into-view';
import initSentry from './plugins/sentry';
import { createVuetify } from './plugins/vuetify';
import App from './App.vue';
import { createRouter } from './router';
import { createStore } from './store';

const { bind, unbind } = vClickOutside.directive;

// экспортируем функцию-фабрику для создания экземпляров нового приложения с роутером и стором
export function createApp({
  cookies = {},
  ostinApiUrl = '/v3',
  bffApiUrl = '/web-api/v1',
  device,
  features,
  ip,
  status,
}) {
  const router = createRouter();
  const vuexStore = createStore();
  const piniaStore = createPinia();

  if (typeof window !== 'undefined') {
    // Только на клиенте
    piniaStore.use(piniaPluginPersistedState);
  }

  const adaptive = new Adaptive(device);

  if (features) {
    vuexStore.state.features = {
      $features: features,
    };
  }

  if (ip) {
    vuexStore.state.config = {
      ip,
      status,
    };
  }

  apiManager.setApi(createApi(createFetcher({
    baseURL: ostinApiUrl,
    getterToken: () => vuexStore.getters['config/ostinAccessToken'],
  }), vuexStore));

  apiManager.setApiBFF(createApiBFF(createFetcher({
    baseURL: bffApiUrl,
    getterToken: () => vuexStore.getters['config/accessToken'],
    actionUpdateToken: () => vuexStore.dispatch('config/updateAccessToken'),
    cookies,
  })));

  // Для обратной совместимости, что бы сейчас все работало как раньше, удалить по возможности
  vuexStore.$api = apiManager.$api;
  vuexStore.$apiBFF = apiManager.$apiBFF;
  vuexStore.$logger = logger;

  const isSSR = typeof window === 'undefined';

  const app = (isSSR ? createSSRApp(App) : createVueApp(App));

  const head = createHead();

  const vuetify = createVuetify();

  app.use(vuexStore);
  app.use(piniaStore);
  app.use(router);
  app.use(head);
  app.use(vuetify);

  app.config.globalProperties.$logger = logger;

  app.provide('adaptive', adaptive);
  app.provide('thirdPartyScriptsDisabled', false);

  app.use(auth, { store: vuexStore, router });
  app.use(cities, { store: vuexStore, adaptive });
  app.use(featureTogglePlugin);
  app.use(navigation, { adaptive });
  app.use(order);
  app.use(kit);
  app.use(ScrollIntoView);

  if (typeof window !== 'undefined') {
    app.use(persEvents, { store: vuexStore, router, adaptive });
    app.provide('$persEvents', app.config.globalProperties.$persEvents);
  }

  app.provide('$auth', app.config.globalProperties.$auth);
  app.provide('$city', app.config.globalProperties.$city);
  app.provide('$nav', app.config.globalProperties.$nav);
  app.provide('$order', app.config.globalProperties.$order);
  app.provide('$logger', app.config.globalProperties.$logger);

  app.use(PerfectScrollbar, {
    options: {
      wheelPropagation: false,
      minScrollbarLength: 50,
    },
  });
  app.use(Vue2TouchEvents, {
    disableClick: true,
    touchClass: '',
    tapTolerance: 10,
    touchHoldTolerance: 400,
    swipeTolerance: 50,
    longTapTimeInterval: 400,
  });

  app.directive('click-outside', {
    mounted(el, bindling) {
      bind(el, { value: bindling.value });
    },
    beforeUnmount(el) {
      unbind(el);
    },
  });

  initSentry(app, router);

  return {
    app,
    router,
    vuexStore,
    piniaStore,
    head,
  };
}
